import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useMultiTickerSearch } from '../hooks/useMultiTickerSearch';
import type { SearchSuggestion } from '../../favorites-app/types';
import { STEP_COPY } from '../constants';

interface OnboardingTickerSearchProps {
  selected: SearchSuggestion[];
  isActive: boolean;
  onSelect: (stock: SearchSuggestion) => void;
  variant?: 'default' | 'footer';
}

export default function OnboardingTickerSearch({
  selected,
  isActive,
  onSelect,
  variant = 'default',
}: OnboardingTickerSearchProps) {
  const isFooter = variant === 'footer';
  const {
    searchValue,
    searchSuggestions,
    isSearchFocused,
    activeSearchIndex,
    searchInputRef,
    searchContainerRef,
    suggestionsListRef,
    handleSearchInputChange,
    handleSearchKeyDown,
    handleSelectStock,
    handleSearchFocus,
  } = useMultiTickerSearch({ selected, isActive, onSelect });

  const showSuggestions =
    searchSuggestions.length > 0 && isSearchFocused && searchValue.trim();

  return (
    <div
      ref={searchContainerRef}
      className={`relative z-20 ${isFooter ? 'mx-auto w-full max-w-[17rem] sm:max-w-xs' : 'w-full'}`}
    >
      <input
        ref={searchInputRef}
        type="text"
        value={searchValue}
        onChange={handleSearchInputChange}
        onKeyDown={handleSearchKeyDown}
        onFocus={handleSearchFocus}
        placeholder={STEP_COPY.companies.searchPlaceholder}
        className={
          isFooter
            ? 'relative w-full rounded-full border-2 border-gray-700 bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 px-4 py-2.5 pr-12 text-sm font-medium text-white transition-all duration-300 placeholder:font-normal placeholder:text-gray-400 hover:border-gray-600 hover:from-gray-700 hover:via-gray-800 hover:to-gray-900 focus:from-gray-700 focus:via-gray-800 focus:to-black focus:outline-none focus:ring-green-500/40'
            : 'w-full rounded-full border-2 border-gray-300 bg-white px-4 py-3 pr-10 text-sm text-gray-900 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-600/20'
        }
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={Boolean(showSuggestions)}
      />

      {isFooter ? (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <div
            className={`flex h-7 w-7 items-center justify-center rounded-full bg-gray-700 transition-all duration-300 ${
              isSearchFocused ? 'bg-green-600/80' : ''
            }`}
          >
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className={`text-sm text-gray-300 transition-all duration-300 ${
                isSearchFocused ? 'scale-110 text-white' : ''
              }`}
            />
          </div>
        </div>
      ) : (
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className={`text-lg transition-all duration-300 ${
              isSearchFocused ? 'scale-110 text-emerald-600' : 'text-gray-400'
            }`}
          />
        </div>
      )}

      {showSuggestions ? (
        <ul
          ref={suggestionsListRef}
          className={`absolute left-0 right-0 z-[100] max-h-60 overflow-y-auto backdrop-blur-md ${
            isFooter
              ? 'rounded-xl border-2 border-gray-700 bg-gray-900 shadow-2xl ring-2 ring-gray-700/50'
              : 'rounded-lg border border-emerald-100 bg-white shadow-xl'
          } top-full mt-2`}
          role="listbox"
        >
          {searchSuggestions.map((stock, index) => {
            const isActiveSuggestion = index === activeSearchIndex;

            return (
              <li key={`${stock.ticker_symbol}-${index}`} role="option" aria-selected={isActiveSuggestion}>
                <button
                  type="button"
                  className={`flex w-full items-center px-4 py-2 text-left transition-all duration-200 ${
                    isFooter
                      ? `hover:bg-gradient-to-r hover:from-gray-800 hover:to-gray-700 ${
                          isActiveSuggestion
                            ? 'border-l-[3px] border-l-green-500 bg-gradient-to-r from-gray-800 to-gray-700 shadow-sm'
                            : ''
                        }`
                      : `hover:bg-emerald-50 ${isActiveSuggestion ? 'bg-emerald-100' : ''}`
                  }`}
                  onClick={() => handleSelectStock(stock)}
                >
                  {stock.logo_url ? (
                    <img
                      src={stock.logo_url}
                      alt=""
                      className={`mr-2 h-5 w-5 flex-shrink-0 ${isFooter ? '' : 'rounded'}`}
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div
                      className={`truncate text-xs font-medium ${
                        isFooter
                          ? isActiveSuggestion
                            ? 'text-white'
                            : 'text-gray-200'
                          : 'text-sm font-semibold text-gray-900'
                      }`}
                    >
                      {stock.ticker_symbol}
                    </div>
                    <div
                      className={`truncate text-xs ${
                        isFooter
                          ? isActiveSuggestion
                            ? 'text-gray-300'
                            : 'text-gray-400'
                          : 'text-gray-600'
                      }`}
                    >
                      {stock.name}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
